import * as React from "react";

import { usePressedState } from "../controls/usePressedState";
import "./pill.css";

/**
 * Pill — the removable chip a multi-select ComboBox shows for each selection.
 *
 * ── IT IS A <button>, WHICH IS WHY IT IS NOT IN THE SELECTION LIST ────────────────────────
 *
 * A pill is not a label with a cross next to it; the whole chip is the remove control. Making
 * it a real button is what gives it Enter/Space, focus and a name for free — and it is exactly
 * why the pills sit in a `role="list"` beside the input rather than inside it: a button cannot
 * live inside a text field.
 *
 * ── THE SELECTABLE-GROUP VARIANT IS NOT PORTED ────────────────────────────────────────────
 *
 * The vendor's Pill can also be a checkbox inside a `PillGroup`, with a check icon and
 * `aria-checked`. Nothing in this application mounts a PillGroup, and porting a selection mode
 * that no call site exercises means shipping untested behaviour that looks supported. If a
 * PillGroup is ever needed, that variant comes back with tests of its own.
 */
export interface PillProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  disabled?: boolean;
}

export const Pill = React.forwardRef<HTMLButtonElement, PillProps>(function Pill(
  { children, className, disabled, onBlur, onClick, onKeyDown, onKeyUp, onPointerDown, ...rest },
  ref,
) {
  /*
   * A SECOND pressed flag, separate from the hook's. The hook clears its own on the next tick
   * unless a key is held, which is right for keys and wrong for a pointer: a press must stay
   * painted for as long as the button is physically held down. This one is cleared by a window
   * listener instead, so a release outside the element still ends the press.
   */
  const [pressActive, setPressActive] = React.useState(false);
  const { active, handlers } = usePressedState({ disabled, onBlur, onClick, onKeyDown, onKeyUp });

  React.useEffect(() => {
    const clear = () => setPressActive(false);
    window.addEventListener("pointerup", clear);
    window.addEventListener("pointercancel", clear);
    return () => {
      window.removeEventListener("pointerup", clear);
      window.removeEventListener("pointercancel", clear);
    };
  }, []);

  const classes = ["saltPill", "saltPill-clickable"];
  if (pressActive || active) classes.push("saltPill-active");
  if (className) classes.push(className);

  return (
    <button
      className={classes.join(" ")}
      data-testid="pill"
      disabled={disabled}
      ref={ref}
      type="button"
      {...handlers}
      onPointerDown={(event) => {
        onPointerDown?.(event);
        if (!disabled) setPressActive(true);
      }}
      {...rest}
    >
      {children}
    </button>
  );
});
